import { type Theme, createTheme } from "@mui/material";
import * as polished from "polished";

import {
  primitiveColors,
  semanticShadows,
} from "@dfp/isomorphic-design-tokens";

import {
  fidooChip,
  fidooFilledInput,
  fidooSelect,
  fidooTab,
  fidooTabs,
  fidooToggleButtonGroup,
} from "./components";
import { defaultFontFamily } from "./fontFamily";
import { palette } from "./palette";
import { typography } from "./typography";

export type StyleOverrideProps = { theme: Omit<Theme, "components"> };

export const themeRebranding = createTheme({
  cssVariables: true,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          backgroundColor: theme.palette.background.main,
          color: theme.palette.text.main,
        },
      }),
    },
    MuiLoadingButton: {
      defaultProps: {
        variant: "contained",
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        color: "secondary",
        variant: "contained",
      },

      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: defaultFontFamily,
          borderRadius: theme.borderRadius.md,
          textTransform: "none",
          textDecoration: "none",
          padding: "8px 16px",
          whiteSpace: "nowrap",
          "& svg": {
            width: "20px",
            height: "20px",
          },
          disabled: ({ theme }: StyleOverrideProps) => ({
            opacity: theme.opacity.disabled,
          }),
          variants: [
            {
              props: { color: "errorSecondary", variant: "contained" },
              style: {
                backgroundColor: theme.palette.errorSecondary.main,
                color: theme.palette.errorSecondary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.errorSecondary.dark,
                },
              },
            },
            {
              props: { color: "emphasis", variant: "contained" },
              style: {
                backgroundColor: theme.palette.custom.fillEmphasis,
                color: theme.palette.text.main,
                "&:hover": {
                  backgroundColor: theme.palette.custom.fillEmphasisHover,
                },
                "&:active": {
                  backgroundColor: theme.palette.custom.fillEmphasisActive,
                },
              },
            },
          ],
        }),
        containedPrimary: ({ theme }) => ({
          "&:hover": {
            background: theme.palette.custom.fillHover,
          },
          "&:active": {
            background: theme.palette.custom.fillActive,
          },
        }),
        containedSecondary: ({ theme }) => ({
          backgroundColor: theme.palette.custom.fillSecondary,
          color: theme.palette.text.main,
          "&:hover": {
            background: theme.palette.custom.fillSecondaryHover,
          },
        }),
        containedSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.success.main,
          "&:hover": {
            background: theme.palette.custom.fillInverse,
          },
        }),
        containedError: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          "&:hover": {
            background: theme.palette.error.dark,
          },
        }),
        textPrimary: ({ theme }) => ({
          color: theme.palette.text.main,
          "&:hover": {
            background: theme.palette.custom.fillSecondaryHover,
          },
        }),
        textSecondary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          "&:hover": {
            background: theme.palette.custom.fillSecondaryHover,
          },
        }),
        textError: ({ theme }) => ({
          color: theme.palette.errorSecondary.contrastText,
          "&:hover": {
            background: theme.palette.errorSecondary.dark,
          },
        }),
        containedSizeSmall: ({ theme }) => ({
          borderRadius: theme.borderRadius.sm,
          padding: "4px 12px",
        }),
        textSizeSmall: ({ theme }) => ({
          borderRadius: theme.borderRadius.sm,
          padding: "4px 12px",
        }),
        containedSizeLarge: ({ theme }) => ({
          borderRadius: theme.borderRadius.lg,
          padding: "12px 24px",
        }),
        textSizeLarge: ({ theme }) => ({
          borderRadius: theme.borderRadius.lg,
          padding: "12px 24px",
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.custom.icon,
          "&:hover": {
            background: theme.palette.custom.fillTertiaryHover,
          },
        }),
        sizeSmall: ({ theme }) => ({
          borderRadius: theme.borderRadius.sm,
          fontSize: "20px",
          padding: "7px",
        }),
        sizeMedium: ({ theme }) => ({
          borderRadius: theme.borderRadius.md,
          fontSize: "20px",
          padding: "10px",
        }),
        sizeLarge: ({ theme }) => ({
          borderRadius: theme.borderRadius.lg,
          fontSize: "20px",
          padding: "14px",
        }),
        colorPrimary: ({ theme }) => ({
          "&:hover": {
            background: theme.palette.custom.fillTertiaryHover,
          },
        }),
        colorSecondary: ({ theme }) => ({
          "&:hover": {
            background: theme.palette.custom.fillTertiaryHover,
          },
        }),
      },
    },
    MuiToggleButtonGroup: fidooToggleButtonGroup,
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: "none",
          backgroundColor: theme.palette.secondary.main,
          "&.Mui-selected": {
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: theme.palette.custom.surfaceTertiary,
            },
          },
          "&.Mui-disabled": {
            border: "none",
          },
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "hover",
        color: "secondary",
      },
      styleOverrides: {
        underlineHover: {
          "&:hover": {
            textDecoration: "none",
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& li:first-of-type a": {
            color: primitiveColors.greyscale[300],
            padding: theme.spacing(0.5),
          },
          "& li:last-of-type a": {
            color: primitiveColors.darkGreen[900],
            padding: theme.spacing(0.5, 1),
            borderRadius: theme.spacing(0.75),
            backgroundColor: primitiveColors.greyscale[50],
            fontWeight: 600,
          },
          "& a": {
            color: primitiveColors.greyscale[600],
            fontWeight: 600,
            padding: theme.spacing(0.5, 1),
            fontSize: theme.typography.body2.fontSize,
            "&:hover": {
              textDecoration: "underline",
            },
          },
          "& svg": {
            height: "20px",
            width: "20px",
          },
        }),
        separator: () => ({
          color: primitiveColors.greyscale[100],
        }),
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 16,
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        rounded: {
          borderRadius: "18px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          height: "40px",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        head: ({ theme }) => ({
          color: theme.palette.text.secondary,
          fontSize: "0.8125rem",
          fontWeight: 400,
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          borderRight: `1px solid ${theme.palette.divider}`,
          "&th:first-of-type": {
            paddingLeft: theme.spacing(4),
          },
          "&th:last-of-type": {
            paddingRight: theme.spacing(4),
          },
        }),
        body: ({ theme }) => ({
          "&td:first-of-type": {
            paddingLeft: theme.spacing(4),
          },
          "&td:last-of-type": {
            paddingRight: theme.spacing(4),
          },
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.main,
        }),
      },
    },
    MuiBadge: {
      defaultProps: {
        color: "primary",
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: defaultFontFamily,
          color: theme.palette.text.main,
          minHeight: "40px",
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.borderRadius.xs,
          "&:hover": {
            backgroundColor: theme.palette.secondary.light,
          },
          "&:hover fieldset.MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.custom.borderHover,
          },
          "&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.custom.borderPrimary,
          },
          "&.Mui-disabled:hover": {
            backgroundColor: "inherit",
          },
          "&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.custom.borderDisabled,
          },
          "&.Mui-error fieldset.MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.custom.borderError,
          },
          "&.Mui-error:hover": {
            backgroundColor: theme.palette.error.light,
          },
        }),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 4,
            sx: {
              borderRadius: (theme) => theme.borderRadius.sm,
            },
          },
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          // the rendering here seems to differ from the docs
          // so we're setting the color explicitly
          ".MuiAutocomplete-popupIndicator": {
            color: theme.palette.text.secondary,
          },
          ".MuiAutocomplete-clearIndicator": {
            color: theme.palette.text.secondary,
          },
        }),
        // same theming as MuiSelect
        paper: ({ theme }) => ({
          ".MuiAutocomplete-listbox": {
            padding: theme.spacing(1),
            maxHeight: 400,
          },
          ".MuiAutocomplete-option": {
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
            borderRadius: theme.spacing(1),
          },
        }),
      },
    },
    MuiSelect: fidooSelect,
    MuiFilledInput: fidooFilledInput,
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&:before": {
            display: "none",
          },
          borderRadius: "8px!important",
          border: `1px solid ${theme.palette.custom.border}`,
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-expanded": {
            borderBottom: `1px solid ${theme.palette.custom.border}`,
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            color: theme.palette.custom.icon,
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "16px",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.main,
          borderRadius: theme.borderRadius.md,
          padding: theme.spacing(1, 2),
          [theme.breakpoints.down("sm")]: {
            flexWrap: "wrap",
          },
        }),
        action: ({ theme }) => ({
          padding: theme.spacing(0, 0, 0, 2),
          marginRight: 0,
        }),
        standardInfo: ({ theme }) => ({
          backgroundColor: theme.palette.info.light,
          border: `1px solid ${theme.palette.info.main}`,
          "& .MuiAlert-icon": {
            color: theme.palette.custom.iconInfo,
          },
        }),
        standardSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.success.light,
          border: `1px solid ${theme.palette.custom.borderSecondarySuccess}`,
          "& .MuiAlert-icon": {
            color: theme.palette.custom.iconSuccess,
          },
        }),
        standardWarning: ({ theme }) => ({
          backgroundColor: theme.palette.warning.light,
          border: `1px solid ${theme.palette.custom.borderSecondaryWarning}`,
          "& .MuiAlert-icon": {
            color: theme.palette.custom.iconWarning,
          },
        }),
        standardError: ({ theme }) => ({
          backgroundColor: theme.palette.error.light,
          border: `1px solid ${theme.palette.custom.borderSecondaryError}`,
          "& .MuiAlert-icon": {
            color: theme.palette.custom.iconError,
          },
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: () => ({
          fontWeight: 600,
        }),
      },
    },
    MuiChip: fidooChip,
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.custom.border}`,
          width: "48px",
          height: "48px",
          borderRadius: "20px",
        }),
        colorDefault: ({ theme }) => ({
          color: theme.palette.text.main,
          backgroundColor: theme.palette.custom.surfaceTertiary,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: theme.customShadows.card,
          borderRadius: theme.borderRadius.md,
          "&:hover": {
            boxShadow: theme.customShadows.cardHover,
          },
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: () => ({
          alignItems: "center",
        }),
        title: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 600,
        }),
        avatar: ({ theme }) => ({
          marginRight: theme.spacing(1.5),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: () => ({
          padding: 0,
          margin: "24px",
        }),
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: () => ({
          padding: 0,
          margin: "24px",
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          background: theme.palette.grey[900],
          color: theme.palette.primary.contrastText,
          fontSize: polished.rem(14),
          fontWeight: 400,
          lineHeight: polished.rem(22),
          padding: theme.spacing(2, 3),
          borderRadius: theme.borderRadius.sm,
          boxShadow: `0px 2px 8px 0px ${theme.palette.grey[500]}`,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: 0,
          marginRight: 0,
          fontSize: theme.typography.body3.fontSize,
          "&.Mui-error": {
            color: theme.palette.errorSecondary.contrastText,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-error": {
            color: theme.palette.text.secondary,
          },
          "&.Mui-focused": {
            color: theme.palette.text.secondary,
          },
        }),
      },
    },
    MuiTabs: fidooTabs,
    MuiTab: fidooTab,
    MuiMenu: {
      styleOverrides: {
        list: ({ theme }) => ({
          padding: theme.spacing(1),
          // This is a hack to prevent added padding in MuiSelect when it scrolls
          paddingRight: theme.spacing(1) + " !important",
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: theme.palette.secondary.light,
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: defaultFontFamily,
          "& .MuiTypography-root": {
            fontFamily: defaultFontFamily,
          },
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: theme.palette.secondary.light,
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: "unset",
          marginRight: theme.spacing(1),
          color: theme.palette.text.main,
          "& svg": {
            width: 20,
            height: 20,
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& td:first-of-type": {
            paddingLeft: theme.spacing(3),
          },
          "& td:last-of-type": {
            paddingRight: theme.spacing(3),
          },
          "& th:first-of-type": {
            paddingLeft: theme.spacing(3),
          },
          "& th:last-of-type": {
            paddingRight: theme.spacing(3),
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.borderRadius.md,
          [theme.breakpoints.down("sm")]: {
            margin: 0,
            width: "100%",
          },
          [theme.breakpoints.up("sm")]: {
            margin: theme.spacing(4),
          },
        }),
        root: ({ theme }) => ({
          "& .MuiBackdrop-root": {
            background: theme.palette.custom.backdrop,
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: 0,
          padding: theme.spacing(4, 9, 3, 4),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(4),
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(0, 4),
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& a": {
            color: theme.palette.info.main,
            textDecoration: "none",
          },
        }),
      },
    },
  },
  avatarSize: {
    56: {
      width: "56px",
      height: "56px",
      borderRadius: "22px",
    },
    48: {
      width: "48px",
      height: "48px",
      borderRadius: "20px",
    },
    40: {
      width: "40px",
      height: "40px",
      borderRadius: "17px",
    },
    32: {
      width: "32px",
      height: "32px",
      borderRadius: "13px",
    },
    28: {
      width: "28px",
      height: "28px",
      borderRadius: "12px",
    },
    24: {
      width: "24px",
      height: "24px",
      borderRadius: "10px",
    },
    20: {
      width: "20px",
      height: "20px",
      borderRadius: "8px",
    },
    16: {
      width: "16px",
      height: "16px",
      borderRadius: "7px",
    },
    12: {
      width: "12px",
      height: "12px",
      borderRadius: "8px",
    },
  },
  borderRadius: {
    xxs: "4px",
    xs: "8px",
    sm: "12px",
    md: "16px",
    lg: "20px",
  },
  opacity: {
    disabled: 0.45,
  },
  customShadows: {
    card: semanticShadows.card,
    cardHover: semanticShadows.cardHover,
    modal: semanticShadows.modal,
    rightPanel: semanticShadows.rightPanel,
    dropdown: semanticShadows.dropdown,
    tab: semanticShadows.tab,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 760,
      md: 1024,
      lg: 1260,
      xl: 1920,
    },
  },
  palette,
  typography,
});
